import React, { CSSProperties, type ReactNode, useMemo } from "react";
import "./styles.scss";
import KC_UIText from "../typography/KC_UIText/KC_UIText";
import KC_UISmall from "../typography/KC_UITextSmall/KC_UISmall";
import { KC_Weights } from "../ds_commons";
import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { buildCls } from "../../utils/helpers/buildCls";

export enum KC_ButtonSize {
  Giant = "giant",
  Large = "lg",
  Medium = "md",
  Small = "sm"
}

export enum KC_ButtonType {
  Submit = "submit",
  Reset = "reset",
  Button = "button"
}

export interface KC_ButtonProps {
  id?: string;
  text?: ReactNode;
  icon?: IconProp;
  innerIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  customIcon?: React.ReactNode;
  extraStyle?: React.CSSProperties;
  disabled?: boolean;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  size?: KC_ButtonSize | "giant" | "lg" | "md" | "sm";
  type?: KC_ButtonType | "button" | "reset" | "submit";
  isPrimary?: boolean;
  isPrimaryLight?: boolean;
  isSecondary?: boolean;
  isInverted?: boolean;
  isTurquoise?: boolean;
  isBlack?: boolean;
  isBordered?: boolean;
  isBorderedBlack?: boolean;
  showIconOnTheLeft?: boolean;
  showIconOnTheRight?: boolean;
  isTransparent?: boolean;
  isUpsell?: boolean;
  spinIcon?: boolean;
  textStyle?: CSSProperties;
  isWhite?: boolean;
}

const KC_Button = (props: KC_ButtonProps) => {
  const InnerIcon = props.innerIcon;

  const btnSize = useMemo(
    () => `btn_${props.size === undefined ? KC_ButtonSize.Medium : props.size}`,
    [props.size === undefined ? KC_ButtonSize.Medium : props.size]
  );

  const buttonClasses = useMemo(() => {
    const cls = [
      "kc_button",
      "align-items-center justify-content-center ",
      // "d-flex align-items-center justify-content-center ",
      btnSize,
      props.disabled === undefined
        ? false
        : props.disabled
          ? "btn_disabled"
          : "",
      props.isUpsell ? "btn_upsell" : "",
      (props.isPrimary === undefined ? !props.isInverted : props.isPrimary) &&
      !props.isInverted &&
      !props.isTransparent &&
      !props.isUpsell
        ? "btn_primary"
        : "",
      props.isPrimaryLight ? "btn_primary_light" : "",
      props.isInverted ? "btn_inverted" : "",
      props.isSecondary ? "btn_secondary" : "",
      props.isTransparent ? "btn_transparent" : "",
      props.isBordered ? "btn_bordered" : "",
      props.isBorderedBlack ? "bordered-black" : "",
      props.className === undefined ? "" : props.className,
      props.isWhite ? "btn_white" : "",
      props.isTurquoise ? "btn_turquoise" : "",
      props.isBlack ? "btn_black" : ""
    ];

    return buildCls(cls);
  }, [
    btnSize,
    props.className === undefined ? "" : props.className,
    props.disabled === undefined ? false : props.disabled,
    props.isPrimary === undefined ? !props.isInverted : props.isPrimary,
    props.isPrimaryLight,
    props.isInverted,
    props.isSecondary,
    props.isTransparent,
    props.isBordered
  ]);

  const TextComponent = useMemo(() => {
    return (props.size === undefined ? KC_ButtonSize.Medium : props.size) ===
      KC_ButtonSize.Small
      ? KC_UISmall
      : KC_UIText;
  }, [props.size === undefined ? KC_ButtonSize.Medium : props.size]);

  const textProps = useMemo(() => {
    return {
      text: props.text as string,
      weight:
        (props.size === undefined ? KC_ButtonSize.Medium : props.size) ===
        KC_ButtonSize.Small
          ? KC_Weights.bold
          : KC_Weights.md
    };
  }, [
    props.text,
    props.size === undefined ? KC_ButtonSize.Medium : props.size
  ]);

  return (
    <button
      id={props.id}
      className={buttonClasses}
      type={props.type === undefined ? KC_ButtonType.Button : props.type}
      onClick={props.onClick}
      disabled={props.disabled === undefined ? false : props.disabled}
      style={props.extraStyle}
    >
      {/* {Icon && <Icon className="btn_icon" />} */}
      {props.showIconOnTheLeft &&
        (props.customIcon ?? (
          <FontAwesomeIcon
            icon={props.icon === undefined ? faTimes : props.icon}
            spin={props.spinIcon}
            style={{ color: "inherit" }}
          />
        ))}
      {props.showIconOnTheLeft && "\u00A0"}
      {props.text && <TextComponent {...textProps} />}
      {props.showIconOnTheRight && "\u00A0"}
      {InnerIcon != null && <InnerIcon className="btn_inner_icon" />}
      {props.showIconOnTheRight &&
        (props.customIcon ?? (
          <FontAwesomeIcon
            icon={props.icon === undefined ? faTimes : props.icon}
          />
        ))}
    </button>
  );
};

export default KC_Button;
